import React, {useState, useEffect, useMemo, useRef} from "react"
// MUI
import {
    Box,
    Button,
    Grid2 as Grid,
    TextField,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Paper, Divider, Skeleton, Stack, FormControlLabel, Switch, Collapse, Alert,
    Menu,
} from "@mui/material";
import {Add, QrCode2, Save, SellRounded} from "@mui/icons-material";
// Data
import {useData} from "@/context/PackagingDataProvider";
// Misc
import {testDomainValidity} from "@/utils";
// Components
import {QRCodeGeneratorDialog} from "@/components/UI";
import {SectionCard, FieldLabel} from "@/components/Layout";
import {packagingTokens as tk} from "@/theme/packagingTokens";
import ProductsGS1Form from "@/components/Products/gs1/ProductsGS1Form";
import Loading from "@/components/Loading/Loading";
import UserSelect from "@/components/UI/UserSelect/UserSelect";
import {useUser} from "@/context/UserProvider";
import {usePermissions} from "@/context/PermissionsProvider";
import {computeVisibleMembers} from "@/utils/productVisibility";

export default function ProductsEditor(props: any) {

    const {
        creating, editing,
        GS1Product, setGS1Product,
        product, setProduct,
        brands, ranges,
        onCreate, onEdit, submitting,
        gs1,
        // Recovered GS1 creation: the QR Code already exists but the Look Up
        // has to be run again — the GS1 form is shown back, data locked when
        // the saved payload carried it (the QR Code encodes its short URL).
        recoveredGS1, recoveredGS1Locked,
        t
    } = props

    const {getSingleQRCode, verifyGTIN, getGS1QRCode} = useData()
    const {fullOrganisation, getFullOrganisation} = useUser()
    const {isAccountRole} = usePermissions()
    const isMember = isAccountRole(["MEMBER"])

    // The product's attribution (locked org + user pick) follows its range,
    // not the caller. A MEMBER of a sub-org can own a range, and any product
    // created under that range must stay in the sub-org — the server already
    // does this on the write path; here we make the UI reflect the same.
    useEffect(() => {
        if (!fullOrganisation) getFullOrganisation()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const orgsFlat = useMemo<Array<{id: string, name: string, users: any[]}>>(() => {
        if (!fullOrganisation) return []
        const list = [{
            id: fullOrganisation.organisation_id,
            name: fullOrganisation.organisation_name,
            users: fullOrganisation.users ?? [],
        }]
        if (Array.isArray(fullOrganisation.sub_organisations)) {
            fullOrganisation.sub_organisations.forEach((sub: any) => {
                list.push({
                    id: sub.organisation_id,
                    name: sub.organisation_name,
                    users: sub.users ?? [],
                })
            })
        }
        return list
    }, [fullOrganisation])

    const selectedRange = Array.isArray(ranges)
        ? ranges.find((r: any) => r.id === product.range)
        : null
    const rangeOrg = selectedRange?.attributed_org_uuid
        ? orgsFlat.find((o) => o.id === selectedRange.attributed_org_uuid)
        : null
    const lockedOrgName =
        selectedRange?.attributed_org_name || rangeOrg?.name || ""
    const scopedUsersBase = rangeOrg?.users ?? []

    // Ghost-pad the users list so the attributed user always has a matching
    // MenuItem — covers the OWNER case, and cases where the product's
    // attributed user isn't in the range's org (cross-org attribution).
    const scopedUsers = useMemo(() => {
        const uuid = product?.attributed_user_uuid
        const email = product?.attributed_user_email
        if (!uuid || !email) return scopedUsersBase
        if ((scopedUsersBase as any[]).some((u: any) => u.user_id === uuid)) return scopedUsersBase
        return [{user_id: uuid, email, role: ""}, ...(scopedUsersBase as any[])]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scopedUsersBase, product?.attributed_user_uuid, product?.attributed_user_email])

    // When the user picks a range (or on edit when the range resolves), default
    // the attributed user to the range's current owner. The user can still
    // override by picking another teammate from the scoped list below.
    useEffect(() => {
        if (isMember) return
        const rangeUser = selectedRange?.attributed_user_uuid
        if (!rangeUser) return
        if (product.attributed_user_uuid) return
        setProduct({...product, attributed_user_uuid: rangeUser})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRange?.attributed_user_uuid])

    const styles = {
        qrcodePreviewTextWrapper: {
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignContent: "center"
        },
    }

    const [selectedBrand, setSelectedBrand] = useState<any>("")
    const [rangesInBrand, setRangesInBrand] = useState<any>([])
    // Anchor for the "+N more" visibility overflow menu. Declared at
    // component scope since hooks inside renderAttributionSection would
    // run conditionally (it's only rendered when attribution is set).
    const [visibleMoreAnchor, setVisibleMoreAnchor] = useState<HTMLElement | null>(null)
    // QR Code
    const [qrcode, setQRCode] = useState<any>(null)
    const [qrcodePreview, setQRCodePreview] = useState<any>(null)
    const [openGenerator, setOpenGenerator] = useState<boolean>(false)
    // Modules
    const [usedModule, setUsedModule] = useState<any>("Standard")
    const [verifiedGtin, setVerifiedGtin] = useState<boolean | string | null>(null)
    // A Look Up happened, then the GS1 payload / URL changed: the verified
    // short URL is stale — prompt the user to re-run the Look Up.
    const [lookUpStale, setLookUpStale] = useState<boolean>(false)
    // Unicode and edge-case domains alert
    const [warningMessage, setWarningMessage] = useState<any>("")
    const [urlWarning, setUrlWarning] = useState<boolean>(false)
    // url warning button disabling
    const [timedOutUrl, setTimedOutUrl] = useState<string | null>(null)
    const [buttonTimeout, setButtonTimeout] = useState<any>(undefined)
    const timeoutInterval = useRef<any>(null)

    // Everything below the parents section stays disabled until BOTH a brand
    // and a range are selected — a product created without a range is broken
    // (and used to leave an unfinishable recovery entry in localStorage).
    // Creation only: editing always has its parents resolved.
    const parentsIncomplete = !editing && (!selectedBrand || !product.range)

    // Creation availability
    const blockQRCodeCreation = (
        parentsIncomplete ||
        !product.name ||
        (!gs1 && !product.default_url) ||
        ((gs1 && !verifiedGtin) || (gs1 && !product.default_url))
    )

    const canProceed = (
        product.name === "" ||
        !brands ||
        !ranges ||
        !product.range ||
        !product.qrcode ||
        (gs1 && !verifiedGtin && !editing)
    )

    // check type
    useEffect(() => {
        if (gs1) setUsedModule("GS1")
        else setUsedModule("Standard")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props])

    // Ranges list hydration on Brand change
    useEffect(() => {
        if (!!selectedBrand) {
            let _rangesInBrand = ranges.filter((range: any) => range.brand.id === selectedBrand)
            setRangesInBrand(_rangesInBrand)
            setProduct({...product, brand: selectedBrand})
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBrand])

    // Brand/Range selector fallback case for edition (and recovery) on range
    // change. The saved range may have been deleted meanwhile — leave the
    // selectors empty then instead of crashing on the missing entry.
    useEffect(() => {
        if (ranges && product.range && selectedBrand === "") {
            let range = ranges.filter((rng: any) => rng.id === product.range)
            if (!!range[0]) {
                setSelectedBrand(range[0].brand.id)
                setProduct({...product, brand: range[0].brand.id})
            } else {
                setProduct({...product, range: ""})
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ranges, product.range])

    // Fetch QR Code preview
    useEffect(() => {
        if (!!product.qrcode && !!product.type && !qrcodePreview) {
            if (product.type === "GS1" || gs1) {
                // GS1 behaviour
                getGS1QRCode(product.qrcode).then((res: any) => {
                    setQRCodePreview(res.preview)
                    setQRCode(res.data)
                })
            } else if (product.type === "Standard" || !gs1 || product.type === "") {
                getSingleQRCode(product.qrcode).then((res: any) => {
                    if (res.data) setQRCode(res.data)
                    if (res.preview) setQRCodePreview(res.preview)
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [product.qrcode, product.type])

    // (Standard) When QR Code is present but default_url isn't
    useEffect(() => {
        if (!!qrcode && !product.default_url) setProduct({...product, default_url: qrcode.full_url})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [qrcode])

    // Interval for warning management
    useEffect(() => {
        if (urlWarning) {
            let duration = 5000
            setButtonTimeout(duration / 1000)
            let interval = 1000

            timeoutInterval.current = setInterval(() => {
                duration = duration - interval
                setButtonTimeout(duration / 1000)
            }, interval)

            return () => clearInterval(timeoutInterval.current)
        } else return undefined
    }, [urlWarning]);

    useEffect(() => {
        if (buttonTimeout === 0) {
            clearInterval(timeoutInterval.current)
            setUrlWarning(false)
        }
    }, [buttonTimeout]);

    const handleQRCodeCreation = () => {

        if (!testDomainValidity(product.default_url) && !urlWarning && timedOutUrl !== product.default_url) {
            setTimedOutUrl(product.default_url)
            setUrlWarning(true)
            setWarningMessage(true)
            return
        } else {
            setOpenGenerator(true)
        }
    }

    // GS1 verification invalidation — a change to the GS1 payload after a
    // Look Up makes the reserved short URL stale. Reset the verification:
    // the Look Up button re-arms (contained variant), QR Code creation is
    // blocked again (blockQRCodeCreation reads verifiedGtin), and an inline
    // prompt asks the user to re-validate. The destination URL is NOT part
    // of the GS1 payload — changing it does not require a new Look Up.
    // Skipped while editing (GS1 data is read-only there) and once the QR
    // exists (the GS1 form is no longer rendered, only the URL field is).
    useEffect(() => {
        if (!gs1 || editing || !!product.qrcode) return
        if (verifiedGtin === null) return // nothing verified yet — nothing to invalidate
        setVerifiedGtin(null)
        setLookUpStale(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [GS1Product])

    const handleGS1LookUp = () => {
        setLookUpStale(false)
        verifyGTIN(GS1Product).then((res: any) => {
            if (res) setVerifiedGtin(res.GS1ShortURL[0].short_url)
            else setVerifiedGtin(false)
        })
    }

    // Specific sections
    const renderParentsForm = () => {
        return (
            <SectionCard title={t("builder_parents")}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
                            {/* Same label icon as the sidebar's "Brands and Ranges" entry */}
                            <SellRounded sx={{fontSize: 22, color: tk.color.textTertiary, flexShrink: 0}} />
                            <FormControl fullWidth required size={"small"}>
                                <InputLabel id={"brand-selector"}>{t("products:parent_brand")}</InputLabel>
                                <Select
                                    fullWidth
                                    label={t("products:parent_brand")}
                                    labelId={"brand-selector"}
                                    onChange={(e) => {
                                        setSelectedBrand(e.target.value)
                                        setProduct({...product, range: ""})
                                    }}
                                    value={selectedBrand}
                                    error={!selectedBrand}
                                >
                                    {!!brands && brands.map((brand: any) => {
                                        return (
                                            <MenuItem key={brand.id} value={brand.id}>{brand.name}</MenuItem>
                                        )
                                    })}
                                </Select>
                                <FormHelperText sx={{ display: selectedBrand ? "none" : "flex" }}>{t("products:select_brand_mandatory")}</FormHelperText>
                            </FormControl>
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Collapse in={selectedBrand !== ""}>
                            <Stack direction={"row"} spacing={1.5} alignItems={"center"}>
                                <SellRounded sx={{fontSize: 22, color: tk.color.textTertiary, flexShrink: 0}} />
                                <FormControl fullWidth size={"small"} sx={{ display: selectedBrand ? "block" : "none" }} required>
                                    <InputLabel id={"range-selector"}>{t("products:parent_range")}</InputLabel>
                                    <Select
                                        fullWidth
                                        label={t("products:parent_range")}
                                        labelId={"range-selector"}
                                        onChange={(e) => setProduct({...product, range: e.target.value})}
                                        value={product.range}
                                        error={!product.range}
                                    >
                                        {!!rangesInBrand && rangesInBrand.map((range: any) => {
                                            return (
                                                <MenuItem key={range.id} value={range.id}>{range.name}</MenuItem>
                                            )
                                        })}

                                        {
                                            (!rangesInBrand || rangesInBrand.length === 0) &&
                                            <MenuItem value={""} disabled>{t("products:no_range_created")}</MenuItem>
                                        }
                                    </Select>
                                    <FormHelperText sx={{ display: product.range ? "none" : "flex" }}>{t("products:select_range_mandatory")}</FormHelperText>
                                </FormControl>
                            </Stack>
                        </Collapse>
                    </Grid>
                </Grid>
            </SectionCard>
        )
    }

    const renderAttributionSection = () => {
        const attributedUuid = product.attributed_user_uuid || ""
        const attributedMember = scopedUsers.find((m: any) => m.user_id === attributedUuid)
        const attributedRole = attributedMember?.role || ""

        const members = scopedUsers.map((m: any) => ({
            id: m.user_id, email: m.email, role: m.role,
        }))
        const visible = attributedUuid
            ? computeVisibleMembers(members, {userId: attributedUuid, role: attributedRole})
            : []

        return (
            <SectionCard title={t("products:attributed_to", "Attributed to")}>
                <Grid container spacing={3}>
                    <Grid size={{xs: 12, md: 6}}>
                        <TextField
                            label={t("products:attribution_org", "Organisation")}
                            size="small"
                            variant="outlined"
                            fullWidth
                            disabled
                            value={lockedOrgName}
                            helperText={t("products:attribution_org_locked", "To change the organisation, edit the brand.")}
                        />
                    </Grid>
                    <Grid size={{xs: 12, md: 6}}>
                        <UserSelect
                            label={t("products:attributed_user", "Attributed user")}
                            value={attributedUuid}
                            users={scopedUsers}
                            disabled={isMember || !selectedRange}
                            helperText={!selectedRange ? t("products:pick_range_first", "Pick a range first") : undefined}
                            onChange={(userUuid) => {
                                if (isMember) return
                                setProduct({...product, attributed_user_uuid: userUuid})
                            }}
                        />
                    </Grid>
                    {attributedUuid && (() => {
                        const INLINE_LIMIT = 3
                        const inline = visible.slice(0, INLINE_LIMIT)
                        const overflow = visible.slice(INLINE_LIMIT)
                        const chipSx = {
                            px: 1, py: 0.25,
                            border: `1px solid ${tk.color.borderLight}`,
                            borderRadius: 999,
                            backgroundColor: tk.color.bg,
                        }
                        return (
                            <Grid size={12}>
                                <Typography variant="body2" sx={{mb: 0.5}}>
                                    <b>{t("products:will_be_visible_to", "Will be visible to")}</b>
                                </Typography>
                                {visible.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">
                                        {t("products:no_one_will_see", "No active user in this organisation will see this product.")}
                                    </Typography>
                                ) : (
                                    <Stack direction="row" spacing={1} sx={{flexWrap: "wrap", gap: 0.5}}>
                                        {inline.map((m) => (
                                            <Typography key={m.id} variant="caption" sx={chipSx}>
                                                {m.email} — {m.role}
                                            </Typography>
                                        ))}
                                        {overflow.length > 0 && (
                                            <Typography
                                                variant="caption"
                                                sx={{...chipSx, cursor: "pointer", fontWeight: 600}}
                                                onClick={(e) => setVisibleMoreAnchor(e.currentTarget)}
                                                aria-haspopup="true"
                                            >
                                                {t("products:will_be_visible_to_more", "+{{count}} more", {count: overflow.length})}
                                            </Typography>
                                        )}
                                        <Menu
                                            open={Boolean(visibleMoreAnchor)}
                                            anchorEl={visibleMoreAnchor}
                                            onClose={() => setVisibleMoreAnchor(null)}
                                            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                                            transformOrigin={{vertical: "top", horizontal: "left"}}
                                            MenuListProps={{dense: true}}
                                        >
                                            {overflow.map((m) => (
                                                <MenuItem key={m.id} sx={{pointerEvents: "none"}}>
                                                    <Typography variant="body2">{m.email} — {m.role}</Typography>
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Stack>
                                )}
                            </Grid>
                        )
                    })()}
                </Grid>
            </SectionCard>
        )
    }

    const renderInformationSection = () => {
        return (
            <SectionCard title={t("builder_info")}>
                <Stack spacing={3}>
                    <Box>
                        <FieldLabel label={t("products:product_name")} required />
                        <TextField
                            error={!parentsIncomplete && product.name === ""}
                            helperText={!parentsIncomplete && product.name === "" ? t("products:product_name_empty_error") : ""}
                            size="small"
                            variant="outlined"
                            fullWidth
                            disabled={parentsIncomplete}
                            value={product.name}
                            onChange={(e) => {
                                setProduct({...product, name: e.target.value})
                            }}/>
                    </Box>
                    <Box>
                        <FieldLabel label={t("products:product_sku")} description={t("products:product_sku_label")} />
                        <TextField
                            size="small"
                            variant="outlined"
                            value={product.sku}
                            fullWidth
                            disabled={parentsIncomplete}
                            onChange={(e) => {
                                setProduct({...product, sku: e.target.value})
                            }}
                        />
                    </Box>
                </Stack>
            </SectionCard>
        )
    }

    const renderPackagingModules = () => {

        const renderModuleOptions = () => {

            const renderGeolocationOptions = () => {
                return (
                    <Grid size={12}>
                        <FieldLabel label={t("products:use_geolocation_webapp")} />
                        <FormControlLabel
                            control={
                                <Switch
                                    disabled={parentsIncomplete || (gs1 && product.bypass_app)}
                                    checked={!product.bypass_app}
                                    onChange={() => setProduct({...product, bypass_app: !product.bypass_app})}
                                />
                            }
                            label={product.bypass_app ? t("products:using_geolocation") : t("products:ignoring_geolocation")}
                        />
                    </Grid>
                )
            }

            const renderGS1Options = () => {

                // A recovered creation keeps its GS1 form visible even though
                // the QR Code exists: the Look Up has to be clicked again.
                if (gs1 && product.qrcode !== "" && !recoveredGS1) return (
                    <Grid size={12}>
                        {renderGeolocationOptions()}
                        <Typography sx={{ mt: 2, ml: 2 }}><i>{t("products:cant_modify_gs1_information")}</i></Typography><br/>
                        { !!product.gtin && <Typography sx={{ mt: 2, ml: 2 }}><b>GTIN: </b>{product.gtin}</Typography>}
                    </Grid>
                )

                return (
                    <>
                        {renderGeolocationOptions()}
                        {(recoveredGS1 && !verifiedGtin) &&
                            <Grid size={12}>
                                <Alert severity={"warning"}>
                                    {t("products:gs1_recovery_lookup_alert")}
                                </Alert>
                            </Grid>
                        }
                        <ProductsGS1Form
                            disabled={parentsIncomplete}
                            readOnlyData={recoveredGS1Locked}
                            {...{usedModule, GS1Product, setGS1Product, handleGS1LookUp, verifiedGtin, lookUpStale, t}}
                        />
                    </>
                )
            }

            if (gs1) return renderGS1Options()
            else return renderGeolocationOptions()
        }

        return (
            <SectionCard title={gs1 ? t("products:gs1_information") : t("products:geolocation_information")}>
                <Grid container spacing={2}>
                    {renderModuleOptions()}
                </Grid>
            </SectionCard>
        )
    }

    const renderDefaultUrlForm = () => {
        return (
            <Collapse in={true}>
                <FieldLabel label={t("products:product_default_url")} description={t("products:product_default_url_label")} />
                <TextField
                    size="small"
                    variant="outlined"
                    value={product.default_url}
                    onChange={(e) => setProduct({...product, default_url: e.target.value})}
                    multiline
                    fullWidth
                    placeholder={"https://"}
                    disabled={parentsIncomplete || (!!product.qrcode && !!qrcode && creating)}
                    error={!parentsIncomplete && (!product.default_url || !testDomainValidity(product.default_url))}
                    helperText={!parentsIncomplete && !product.default_url && t("products:default_url_mandatory")}
                />
                <Collapse in={warningMessage || (editing && !testDomainValidity(product.default_url))} sx={{ mt: 2}}>
                    <Alert severity={"warning"}>
                        {t("products:qrcode_url_warning")}
                    </Alert>
                </Collapse>
                <Divider sx={{ my: 2 }} />
            </Collapse>

        )
    }

    const renderQRCodeForm = () => {

        const renderQRCodePreview = () => {
            if (qrcodePreview) return <img src={qrcodePreview} width="100px" height="100px" alt={"qrcode-preview"}/>
            else return <Skeleton height={100} width={100} variant={"rectangular"} />
        }

        const renderQRCodeFormContent = () => {
            if ((!!product.qrcode && !!qrcode) || (!!product.qrcode && qrcodePreview && product.type === "GS1")) {
                return (
                    <Paper variant={"outlined"} sx={{ p: 2, borderRadius: tk.radius.card, borderColor: tk.color.border }}>
                        <Grid container justifyContent={{ xs: "center", md: "flex-start"}} spacing={5}>
                            <Grid size={{ xs: 10, md: "auto" }}>
                                {renderQRCodePreview()}
                            </Grid>
                            <Grid size={{ xs: 10, md: 6 }} sx={styles.qrcodePreviewTextWrapper}>
                                { creating && <Typography fontWeight={700}>{t("products:qrcode_successfully_created")}</Typography> }
                                <Typography>{qrcode?.label}</Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                )
            } else {
                return (
                    <Button
                        onClick={() => handleQRCodeCreation()}
                        disabled={blockQRCodeCreation || buttonTimeout}
                        variant={"contained"}
                        color={"secondary"}
                        disableElevation
                        size={"large"}
                        fullWidth
                        startIcon={<QrCode2 />}
                    >
                        {
                            (urlWarning && !!buttonTimeout) ? t("timeout") + " " + buttonTimeout + t("seconds")
                            : parentsIncomplete ? t("products:select_parents_before_qrcode")
                            : (gs1 && !verifiedGtin) ? t("products:look_up_before_creating_qrcode")
                            : !product.name ? t("products:name_product_before_qrcode")
                            : t("products:create_product_qrcode")
                        }
                    </Button>
                )
            }
        }

        return (
            <>
                <FieldLabel label={t("products:product_qrcode")} description={t("products:product_qrcode_label")} />
                { (gs1 && !product.qrcode) &&
                    <Typography color={"error"} variant="body2" gutterBottom>
                        {t("products:gs1_edition_alert")}
                    </Typography>
                }
                {renderQRCodeFormContent()}
            </>
        )
    }

    // loading
    if (editing && product.id === "") return <Loading />

    return (
        <>
            <Stack spacing={2.5}>
                {renderParentsForm()}
                {renderInformationSection()}
                {renderAttributionSection()}
                {renderPackagingModules()}

                <SectionCard title={t("builder_destination")}>
                    {renderDefaultUrlForm()}
                    {renderQRCodeForm()}
                </SectionCard>

                <Box sx={{display: "flex", justifyContent: "flex-end", pt: 1}}>
                    <Button
                        onClick={creating ? onCreate : editing ? onEdit : () => {return}}
                        // Guard against a double submit: without this a second
                        // click fires another POST, and product creation is not
                        // idempotent.
                        disabled={canProceed || submitting}
                        variant={"contained"}
                        disableElevation
                        size={"large"}
                        startIcon={editing ? <Save /> : <Add />}
                        sx={{textTransform: "none", fontWeight: 600, borderRadius: tk.radius.sm, px: 4}}
                    >
                        {creating ? t("products:finalize") : editing ? t("products:product_edit") : t("common:loading")}
                    </Button>
                </Box>
            </Stack>

            <QRCodeGeneratorDialog
                open={openGenerator}
                onClose={() => setOpenGenerator(false)}
                {...{
                    gs1,
                    product, setProduct,
                    usedModule,
                    verifiedGtin,
                    t
                }}
            />
        </>
    )
}